/**
 * EventPreviewCard
 *
 * Card de pré-visualização de um evento (usado no tooltip de hover da Agenda).
 * Design derivado do mockup 09 ("Espelho Vivo"): faixa da cor, badge de tipo +
 * status, título, responsável, data/hora, local, Meet, contato vinculado e
 * participantes. Tema claro fixo (como modais/tooltips do produto).
 */

import React from 'react';
import { CalendarEventResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { getEventLocalDate } from '../utils/eventDate';

export interface EventPreviewCardProps {
  event: CalendarEventResponse;
  ownerName?: string;
  contactName?: string;
  contactPhone?: string;
}

const TYPE_LABEL: Record<string, string> = {
  meeting: 'Reunião',
  task: 'Tarefa',
  reminder: 'Lembrete',
  follow_up: 'Retorno',
  other: 'Outro',
  birthday: 'Aniversário',
  activity: 'Atividade',
};

const STATUS_META: Record<string, { label: string; color: string }> = {
  confirmed: { label: 'Confirmado', color: '#16a34a' },
  tentative: { label: 'Tentativo', color: '#d97706' },
  cancelled: { label: 'Cancelado', color: '#dc2626' },
};

const WEEK_DAYS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const eventColor = (colorId?: string, eventType?: string): string => {
  if (eventType === 'birthday') return '#10b981';
  if (eventType === 'activity') return '#f97316';
  const colors: Record<string, string> = {
    '1': '#a4bdfc', '2': '#7ae7bf', '3': '#dbadff', '4': '#ff887c', '5': '#fbd75b',
    '6': '#ffb878', '7': '#46d6db', '8': '#e1e1e1', '9': '#5484ed', '10': '#51b749', '11': '#dc2127',
  };
  return colors[colorId || '9'] || colors['9'];
};

const initials = (name?: string): string => {
  const n = (name || '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/);
  const raw = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
  return raw.toUpperCase();
};

const pad = (v: number): string => v.toString().padStart(2, '0');

const durationLabel = (start: Date, end: Date): string => {
  const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const EVP_CSS = `
.evp{ --ink:#1e293b; --ink-2:#334155; --muted:#6b7280; --faint:#94a3b8;
  --line:rgba(15,23,42,.08); --purple-600:#7c3aed;
  width:320px; background:#fff; border:1px solid rgba(15,23,42,.08); border-radius:16px; overflow:hidden;
  font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif; color:var(--ink-2);
  box-shadow:0 8px 18px -6px rgba(15,23,42,.22), 0 24px 48px -18px rgba(15,23,42,.28);
  animation:evp-in .14s ease both; }
@keyframes evp-in{ from{opacity:0;transform:translateY(4px);} to{opacity:1;transform:none;} }
.evp *{ box-sizing:border-box; }
.evp-stripe{ height:5px; }
.evp-pad{ padding:14px 16px 16px; }
.evp-top{ display:flex;align-items:center;gap:8px;margin-bottom:9px; }
.evp-type{ font-size:11px;font-weight:700;color:#475569;background:#f1f5f9;
  padding:2px 8px;border-radius:999px; }
.evp-status{ display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:550;margin-left:auto; }
.evp-dot{ width:7px;height:7px;border-radius:50%; }
.evp-title{ font-size:15.5px;font-weight:650;line-height:1.3;letter-spacing:-.01em;color:var(--ink);
  margin:0 0 3px;overflow-wrap:anywhere; }
.evp-owner{ display:flex;align-items:center;gap:7px;font-size:12.5px;color:var(--muted); }
.evp-av{ border-radius:50%;display:grid;place-items:center;color:#fff;font-weight:600;flex:0 0 auto; }
.evp-av-owner{ width:20px;height:20px;font-size:9px;background:linear-gradient(135deg,#8b5cf6,#6366f1); }
.evp-av-contact{ width:30px;height:30px;font-size:11px;background:linear-gradient(135deg,#f472b6,#a855f7); }
.evp-av-g{ width:24px;height:24px;font-size:9.5px;background:linear-gradient(135deg,#38bdf8,#6366f1);
  box-shadow:0 0 0 2px #fff; }
.evp-hr{ height:1px;background:var(--line);margin:12px 0; }
.evp-rows{ display:flex;flex-direction:column;gap:10px; }
.evp-row{ display:flex;align-items:flex-start;gap:10px; }
.evp-ri{ color:var(--faint);flex:0 0 auto;margin-top:1px;display:grid; }
.evp-r1{ font-size:13px;color:var(--ink-2);line-height:1.35; }
.evp-r2{ font-size:11.5px;color:var(--muted);margin-top:1px; }
.evp-meet{ display:flex;align-items:center;gap:9px;margin-top:12px;padding:9px 11px;border-radius:10px;
  background:#f5f3ff;border:1px solid #ede9fe; }
.evp-meet .evp-ri{ color:var(--purple-600); }
.evp-meet-t{ font-size:12px;font-weight:600;color:var(--purple-600); }
.evp-meet-l{ font-size:11.5px;color:var(--muted);word-break:break-all;line-height:1.3; }
.evp-block{ margin-top:12px; }
.evp-block-h{ font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--faint);margin-bottom:7px; }
.evp-contact{ display:flex;align-items:center;gap:10px; }
.evp-cname{ font-size:13px;font-weight:600;color:var(--ink); }
.evp-cphone{ font-size:11.5px;color:var(--muted);margin-top:1px; }
.evp-crm{ font-size:8.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--purple-600);
  background:#fff;border:1px solid #ddd6fe;padding:1px 6px;border-radius:999px;margin-left:6px; }
.evp-guests{ display:flex;align-items:center;gap:9px; }
.evp-avatars{ display:flex; }
.evp-avatars .evp-av-g + .evp-av-g{ margin-left:-8px; }
.evp-gmore{ width:24px;height:24px;border-radius:50%;background:#eef2f7;color:#64748b;font-size:10px;font-weight:600;
  display:grid;place-items:center;box-shadow:0 0 0 2px #fff;margin-left:-8px; }
.evp-gtext{ font-size:12px;color:var(--muted); }
`;

export const EventPreviewCard: React.FC<EventPreviewCardProps> = ({ event, ownerName, contactName, contactPhone }) => {
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  const validStart = !isNaN(start.getTime());
  const validEnd = !isNaN(end.getTime());

  const color = eventColor(event.colorId, event.eventType);
  const typeLabel = TYPE_LABEL[event.eventType || 'meeting'] || 'Evento';
  const statusMeta = STATUS_META[event.status || 'confirmed'] || STATUS_META.confirmed;

  // All-day é date-only (meia-noite UTC); usa a parte-data p/ o rótulo não deslocar
  // o dia em fusos negativos (senão 09/07 apareceria como 08/07 no GMT-3).
  const displayStart = event.allDay && validStart ? getEventLocalDate(event) : start;
  const dateLabel = validStart
    ? `${WEEK_DAYS[displayStart.getDay()]}, ${displayStart.getDate()} de ${MONTHS[displayStart.getMonth()]} de ${displayStart.getFullYear()}`
    : '';
  const timeLabel = event.allDay
    ? 'Dia inteiro'
    : validStart && validEnd
      ? `${pad(start.getHours())}:${pad(start.getMinutes())} – ${pad(end.getHours())}:${pad(end.getMinutes())} · ${durationLabel(start, end)}`
      : '';

  const meetLink = event.conferenceData?.link;
  const attendees = event.attendees || [];
  const shownGuests = attendees.slice(0, 4);
  const extraGuests = attendees.length - shownGuests.length;

  return (
    <div className="evp" role="tooltip">
      <style>{EVP_CSS}</style>
      <div className="evp-stripe" style={{ background: color }} />
      <div className="evp-pad">
        <div className="evp-top">
          <span className="evp-type">{typeLabel}</span>
          <span className="evp-status" style={{ color: statusMeta.color }}>
            <span className="evp-dot" style={{ background: statusMeta.color }} />
            {statusMeta.label}
          </span>
        </div>

        <h3 className="evp-title">{event.summary}</h3>

        {ownerName && (
          <div className="evp-owner">
            <span className="evp-av evp-av-owner" aria-hidden="true">{initials(ownerName)}</span>
            Organizado por <strong style={{ fontWeight: 600, color: '#475569' }}>{ownerName}</strong>
          </div>
        )}

        <div className="evp-hr" />

        <div className="evp-rows">
          {(dateLabel || timeLabel) && (
            <div className="evp-row">
              <span className="evp-ri"><Icon name="clock" className="w-[15px] h-[15px]" /></span>
              <div>
                {dateLabel && <div className="evp-r1">{dateLabel}</div>}
                {timeLabel && <div className="evp-r2">{timeLabel}</div>}
              </div>
            </div>
          )}

          {event.location && (
            <div className="evp-row">
              <span className="evp-ri"><Icon name="map-pin" className="w-[15px] h-[15px]" /></span>
              <div><div className="evp-r1">{event.location}</div></div>
            </div>
          )}

          {event.recurrence && event.recurrence.length > 0 && (
            <div className="evp-row">
              <span className="evp-ri"><Icon name="repeat" className="w-[15px] h-[15px]" /></span>
              <div><div className="evp-r1">Evento recorrente</div></div>
            </div>
          )}
        </div>

        {meetLink && (
          <div className="evp-meet">
            <span className="evp-ri"><Icon name="video" className="w-[16px] h-[16px]" /></span>
            <div style={{ minWidth: 0 }}>
              <div className="evp-meet-t">Google Meet</div>
              <div className="evp-meet-l">{meetLink.replace(/^https?:\/\//, '')}</div>
            </div>
          </div>
        )}

        {contactName && (
          <div className="evp-block">
            <div className="evp-block-h">Contato vinculado</div>
            <div className="evp-contact">
              <span className="evp-av evp-av-contact" aria-hidden="true">{initials(contactName)}</span>
              <div style={{ minWidth: 0 }}>
                <div>
                  <span className="evp-cname">{contactName}</span>
                  <span className="evp-crm">CRM</span>
                </div>
                {contactPhone && <div className="evp-cphone">{contactPhone}</div>}
              </div>
            </div>
          </div>
        )}

        {attendees.length > 0 && (
          <div className="evp-block">
            <div className="evp-block-h">Participantes</div>
            <div className="evp-guests">
              <div className="evp-avatars">
                {shownGuests.map((a, i) => (
                  <span className="evp-av evp-av-g" key={`${a.email || a.displayName || 'g'}-${i}`} aria-hidden="true">
                    {initials(a.displayName || a.email)}
                  </span>
                ))}
                {extraGuests > 0 && <span className="evp-gmore">+{extraGuests}</span>}
              </div>
              <span className="evp-gtext">{attendees.length} convidado{attendees.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

EventPreviewCard.displayName = 'EventPreviewCard';
